import { format } from 'date-fns';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import EmojiPicker from 'emoji-picker-react';
import { io } from 'socket.io-client';
import { BACKEND_SERVER_URL } from '../../../http/http-config';

const LiveStreamingChatModal = forwardRef((props, ref) => {
    const socket = useRef(null);
    const [show, setShow] = useState(false);
    const [stream, setStream] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const pickerRef = useRef(null);
    const userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
    const [replyTo, setReplyTo] = useState(null);
    const [showMentions, setShowMentions] = useState(false);
    const [mentionList, setMentionList] = useState([]);
    const [activeMentionIndex, setActiveMentionIndex] = useState(0);

    const inputRef = useRef(null);


    useImperativeHandle(ref, () => ({
        openModal(selectedStream) {
            setStream(selectedStream || null);
            setShow(true);
        }
    }));

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages.length]);

    useEffect(() => {
        if (!stream) return;

        socket.current = io(BACKEND_SERVER_URL, {
            transports: ['websocket'],
            withCredentials: true,
            auth: {
                email: userDetails.EmailId,
                name: userDetails.FirstName,
                role: 'admin'
            }
        });
        socket.current.on('connect', () => {
            console.log('Connected to socket server');
            socket.current.emit('joinRoom', stream.Sn);
        });
        socket.current.on('roomUsers', (users) => {
            setMentionList(users);
        });

        const handleChatHistory = (messages) => {
            setMessages(messages);
        };

        const handleChatMessage = (newMsg) => {
            console.log('New message received:', newMsg);
            setMessages((prevMessages) => [...prevMessages, newMsg]);
        };
        socket.current.on('chatHistory', handleChatHistory);
        socket.current.on('chatMessage', handleChatMessage);

        // Cleanup on unmount or stream change
        return () => {
            socket.current.off('chatMessage', handleChatMessage);
            socket.current.off('roomUsers');
            socket.current.off('chatHistory', handleChatHistory);
            socket.current.disconnect();
        };
    }, [stream]);

    const onEmojiClick = (emojiData) => {
        setInput((prevInput) => prevInput + emojiData.emoji);
        // Optional: Keep picker open or close it
        // setShowEmojiPicker(false); 
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSend = async () => {
        if (!input.trim()) return;

        try {
            setLoading(true);
            const msg = {
                ClassId: stream.Sn,
                SenderEmail: userDetails.EmailId,
                SenderName: userDetails.FirstName,
                SenderRole: 'admin',
                Message: input.trim(),
                CreatedAt: new Date(),

                ReplyToMessageId: replyTo?.Sn || null,
                ReplyToMessageText: replyTo?.Message || null,
                ReplyToSenderName: replyTo?.SenderName || null
            };
            // const msg = {
            //     ClassId: stream.Sn,
            //     SenderEmail: userDetails.EmailId,
            //     SenderName: userDetails.FirstName,
            //     SenderRole: 'admin',
            //     Message: input.trim(),
            //     CreatedAt: new Date()
            // }
            socket.current.emit('chatMessage', msg);
            setInput('');

            setReplyTo(null);
            setShowMentions(false);
        } catch (error) {
            console.error('Failed to send message:', error);
            alert('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    const renderMessage = (text, mentions) => {
        if (!mentions) return text;

        // ✅ Convert JSON string → array
        let mentionList = [];

        if (typeof mentions === 'string') {
            try {
                mentionList = JSON.parse(mentions);
            } catch {
                mentionList = [];
            }
        } else if (Array.isArray(mentions)) {
            mentionList = mentions;
        }

        if (!mentionList.length) return text;

        const parts = [];
        let remaining = text;

        mentionList.forEach((name, idx) => {
            const mention = `@${name}`;
            const index = remaining.indexOf(mention);

            if (index !== -1) {
                parts.push(remaining.slice(0, index));
                parts.push(
                    <span key={`${name}-${idx}`} style={{
                        background: '#fff3cd',
                        color: '#856404',
                        padding: '0 4px',
                        borderRadius: '4px',
                        fontWeight: '600'
                    }}>
                        {mention}
                    </span>
                );
                remaining = remaining.slice(index + mention.length);
            }
        });

        parts.push(remaining);
        return parts;
    };
    useEffect(() => {
        if (inputRef.current) {
            // 1. Reset height to auto to get the correct scrollHeight for the new content
            inputRef.current.style.height = 'auto';

            // 2. Set the height to the scrollHeight
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
        }
    }, [input]);

    return (
        <Modal show={show} onHide={() => setShow(false)} size="lg" backdrop="static">
            <div className="modal-content">

                {/* Header */}
                <div className="modal-header" style={{ padding: '0.5rem 0.75rem' }}>
                    <h6 className="modal-title mb-0">
                        Chat – {stream?.Title || 'Stream'}
                    </h6>
                    <button
                        type="button"
                        className="btn-close"
                        style={{ fontSize: 12 }}
                        onClick={() => setShow(false)}
                    />
                </div>

                {/* Body */}
                <div
                    className="modal-body position-relative"
                    style={{
                        maxHeight: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0.5rem'
                    }}
                >
                    {/* Emoji Picker Overlay */}
                    {showEmojiPicker && (
                        <div ref={pickerRef} style={{ position: 'absolute', top: '50px', right: '0', zIndex: 1000 }}>
                            <EmojiPicker onEmojiClick={onEmojiClick} theme="light" emojiStyle="native" />
                        </div>
                    )}

                    {/* Messages */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            border: '1px solid #eee',
                            padding: '0.5rem',
                            borderRadius: 6,
                            background: '#fafafa'
                        }}
                    >
                        {messages.length === 0 ? (
                            <div
                                style={{
                                    color: '#888',
                                    textAlign: 'center',
                                    paddingTop: '1rem',
                                    fontSize: 13
                                }}
                            >
                                No messages yet. Say hello 👋
                            </div>
                        ) : (
                            messages.map((m, index) => {
                                const myMessage = m.SenderEmail === userDetails.EmailId;
                                const showSeparator = index > 0 && messages[index - 1].SenderEmail !== m.SenderEmail;

                                return (
                                    <div
                                        onContextMenu={(e) => {
                                            if (m.SenderEmail === userDetails.EmailId) return;
                                            e.preventDefault();
                                            setReplyTo({
                                                Sn: m.Sn,
                                                Message: m.Message,
                                                SenderName: m.SenderName
                                            });
                                            inputRef.current.focus();
                                        }}
                                        key={m.Sn || index} style={{ width: '100%' }}>
                                        {showSeparator && (
                                            <div
                                                style={{
                                                    height: '1px',
                                                    backgroundColor: '#ccc',
                                                    margin: '0px 0px 6px',
                                                    width: '100%'
                                                }}
                                            />
                                        )}
                                        <div
                                            style={{
                                                marginBottom: 6,
                                                display: 'flex',
                                                justifyContent: myMessage ? 'flex-end' : 'flex-start'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    maxWidth: '75%',
                                                    padding: '4px 6px',
                                                    borderRadius: 6,
                                                    backgroundColor: myMessage ? '#007bff' : '#15803d',
                                                    color: '#fff',
                                                    borderBottomLeftRadius: myMessage ? 6 : 0,
                                                    borderBottomRightRadius: myMessage ? 0 : 6,
                                                    lineHeight: 1.3,

                                                    /* 🔥 IMPORTANT: LINE WRAPPING FIX */
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word'
                                                }}
                                            >
                                                {!myMessage && (
                                                    <div
                                                        style={{
                                                            fontSize: 11,
                                                            borderBottom: '1px solid #ccc',
                                                            fontWeight: 600,
                                                            marginBottom: 2
                                                        }}
                                                    >
                                                        {m.SenderName}
                                                    </div>
                                                )}
                                                {m.ReplyToMessageText && (
                                                    <div style={{
                                                        background: 'rgba(255,255,255,0.2)',
                                                        padding: '3px',
                                                        borderLeft: '2px solid #fff',
                                                        marginBottom: 3,
                                                        fontSize: 11
                                                    }}>
                                                        <strong>{m.ReplyToSenderName}</strong>
                                                        <div>{m.ReplyToMessageText}</div>
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 13 }}>
                                                    {renderMessage(m.Message, m.Mentions)}
                                                </div>

                                                <div
                                                    style={{
                                                        fontSize: 10,
                                                        marginTop: 2,
                                                        opacity: 0.6,
                                                        textAlign: 'right'
                                                    }}
                                                >
                                                    {format(new Date(m.CreatedAt), 'hh:mm a')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <div style={{ position: 'relative', flex: 1, height: 'fit-content' }}>
                            {replyTo && (
                                <div style={{
                                    background: '#eee',
                                    padding: '4px 6px',
                                    borderLeft: '3px solid #007bff',
                                    marginBottom: 6,
                                    fontSize: 12
                                }}>
                                    <strong>{replyTo.SenderName}</strong>
                                    <div>{replyTo.Message}</div>
                                    <span
                                        style={{ cursor: 'pointer', float: 'right' }}
                                        onClick={() => setReplyTo(null)}
                                    >✖</span>
                                </div>
                            )}
                            {showMentions && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '45px',
                                    background: '#fff',
                                    border: '1px solid #ccc',
                                    width: '200px',
                                    zIndex: 1000
                                }}>
                                    {mentionList.map((u, idx) => (
                                        <div
                                            key={u.email}
                                            style={{
                                                padding: 6,
                                                cursor: 'pointer',
                                                background: idx === activeMentionIndex ? '#e5f0ff' : 'transparent',
                                                fontWeight: idx === activeMentionIndex ? 600 : 400
                                            }}
                                            onMouseEnter={() => setActiveMentionIndex(idx)}
                                            onClick={() => {
                                                setInput(prev => {
                                                    const parts = prev.split(' ');
                                                    parts.pop();
                                                    return [...parts, `@${u.name}`].join(' ') + ' ';
                                                });
                                                setShowMentions(false);
                                            }}
                                        >
                                            {u.name}
                                        </div>
                                    ))}

                                </div>
                            )}
                            <textarea
                                id='chat-input'
                                name='chat-input'
                                style={{
                                    resize: 'none',
                                    overflow: 'auto',
                                    minHeight: '50px',
                                    maxHeight: '200px',
                                    fontSize: 13,
                                    padding: '6px 35px 6px 8px',
                                    lineHeight: '1.4',
                                    boxSizing: 'border-box'
                                }}
                                rows={3}
                                ref={inputRef}
                                className="form-control"
                                value={input}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setInput(value);

                                    const lastWord = value.split(' ').pop();
                                    if (lastWord.startsWith('@')) {
                                        socket.current.emit('getRoomUsers', stream.Sn);
                                        setShowMentions(true);
                                        setActiveMentionIndex(0);
                                    } else {
                                        setShowMentions(false);
                                    }
                                }}
                                placeholder="Type a message..."
                                onKeyDown={(e) => {
                                    if (!showMentions) {
                                        if (e.key === 'Enter' && !loading) {
                                            if (e.shiftKey) {
                                                e.preventDefault();
                                                setInput(prev => prev + '\n');
                                            } else {
                                                e.preventDefault();
                                                handleSend();
                                            }
                                        }
                                        return;
                                    }

                                    if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        setActiveMentionIndex((prev) =>
                                            Math.min(prev + 1, mentionList.length - 1)
                                        );
                                    }

                                    if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        setActiveMentionIndex((prev) =>
                                            Math.max(prev - 1, 0)
                                        );
                                    }

                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const user = mentionList[activeMentionIndex];
                                        if (!user) return;

                                        setInput(prev => {
                                            const parts = prev.split(' ');
                                            parts.pop();
                                            return [...parts, `@${user.name}`].join(' ') + ' ';
                                        });

                                        setShowMentions(false);
                                    }

                                    if (e.key === 'Escape') {
                                        setShowMentions(false);
                                    }
                                }}

                                disabled={loading}
                            ></textarea>
                            {/* Emoji Trigger Icon inside Input */}
                            <span
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                😀
                            </span>
                        </div>
                        <button
                            className="btn btn-primary"
                            style={{ padding: '6px 12px', fontSize: 13 }}
                            onClick={handleSend}
                            disabled={loading}
                        >
                            {loading ? '...' : 'Send'}
                        </button>
                    </div>

                </div>
            </div>
        </Modal >


    );
});

export default LiveStreamingChatModal;
